'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────
type GemType = 'ruby' | 'sapphire' | 'emerald' | 'gold' | 'amethyst' | 'diamond' | 'bomb';
type Phase = 'intro' | 'playing' | 'ability' | 'result';

interface Gem {
  type: GemType;
  id: string;
  matched?: boolean;
  falling?: boolean;
}

interface AbilityCard {
  id: string;
  name: string;
  emoji: string;
  description: string;
  color: string;
  uses: number;
  maxUses: number;
  action: 'row_wipe' | 'col_wipe' | 'gem_clear' | 'bomb_drop' | 'color_blast';
}

interface StickerSlot {
  id: string;
  emoji: string;
  label: string;
  filled: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const GRID_SIZE = 7;
const GEM_EMOJIS: Record<GemType, string> = {
  ruby: '🔴',
  sapphire: '🔵',
  emerald: '🟢',
  gold: '🟡',
  amethyst: '🟣',
  diamond: '⚪',
  bomb: '💣',
};
const GEM_COLORS: Record<GemType, string> = {
  ruby: '#ff3b6b',
  sapphire: '#4488ff',
  emerald: '#36e2a4',
  gold: '#ffd23f',
  amethyst: '#a78bfa',
  diamond: '#e2e8f0',
  bomb: '#ff9500',
};
const GEM_TYPES: GemType[] = ['ruby', 'sapphire', 'emerald', 'gold', 'amethyst', 'diamond'];

const INITIAL_ABILITIES: AbilityCard[] = [
  { id: 'row_wipe', name: 'Row Blast', emoji: '💥', description: 'Wipe an entire row', color: '#ff3b6b', uses: 1, maxUses: 1, action: 'row_wipe' },
  { id: 'col_wipe', name: 'Column Crush', emoji: '⚡', description: 'Destroy a full column', color: '#ffd23f', uses: 0, maxUses: 1, action: 'col_wipe' },
  { id: 'gem_clear', name: 'Gem Sweep', emoji: '🌀', description: 'Clear all gems of one color', color: '#a78bfa', uses: 0, maxUses: 1, action: 'gem_clear' },
  { id: 'bomb_drop', name: 'Bomb Drop', emoji: '💣', description: 'Explode a 3×3 area', color: '#ff9500', uses: 0, maxUses: 1, action: 'bomb_drop' },
];

const STICKER_ALBUM: StickerSlot[] = [
  { id: 's1', emoji: '🌟', label: 'First Match', filled: false },
  { id: 's2', emoji: '🔥', label: '3-in-a-row', filled: false },
  { id: 's3', emoji: '💎', label: 'Diamond Found', filled: false },
  { id: 's4', emoji: '⚡', label: 'Speed Match', filled: false },
  { id: 's5', emoji: '🎯', label: 'Precision', filled: false },
  { id: 's6', emoji: '👑', label: 'Duel Winner', filled: false },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function randomGem(): GemType {
  return GEM_TYPES[Math.floor(Math.random() * GEM_TYPES.length)];
}

function makeGrid(): Gem[][] {
  return Array.from({ length: GRID_SIZE }, (_, r) =>
    Array.from({ length: GRID_SIZE }, (_, c) => ({
      type: randomGem(),
      id: `${r}-${c}-${Math.random().toString(36).slice(2, 6)}`,
    }))
  );
}

function findMatches(grid: Gem[][]): Set<string> {
  const matched = new Set<string>();
  // Horizontal
  for (let r = 0; r < GRID_SIZE; r++) {
    for (let c = 0; c <= GRID_SIZE - 3; c++) {
      const t = grid[r][c].type;
      if (t !== 'bomb' && grid[r][c + 1].type === t && grid[r][c + 2].type === t) {
        matched.add(`${r}-${c}`);
        matched.add(`${r}-${c + 1}`);
        matched.add(`${r}-${c + 2}`);
        if (c + 3 < GRID_SIZE && grid[r][c + 3].type === t) matched.add(`${r}-${c + 3}`);
        if (c + 4 < GRID_SIZE && grid[r][c + 4].type === t) matched.add(`${r}-${c + 4}`);
      }
    }
  }
  // Vertical
  for (let c = 0; c < GRID_SIZE; c++) {
    for (let r = 0; r <= GRID_SIZE - 3; r++) {
      const t = grid[r][c].type;
      if (t !== 'bomb' && grid[r + 1][c].type === t && grid[r + 2][c].type === t) {
        matched.add(`${r}-${c}`);
        matched.add(`${r + 1}-${c}`);
        matched.add(`${r + 2}-${c}`);
        if (r + 3 < GRID_SIZE && grid[r + 3][c].type === t) matched.add(`${r + 3}-${c}`);
        if (r + 4 < GRID_SIZE && grid[r + 4][c].type === t) matched.add(`${r + 4}-${c}`);
      }
    }
  }
  return matched;
}

function collapseGrid(grid: Gem[][]): Gem[][] {
  const next = grid.map(row => [...row]);
  for (let c = 0; c < GRID_SIZE; c++) {
    let col: Gem[] = [];
    for (let r = 0; r < GRID_SIZE; r++) {
      if (!next[r][c].matched) col.push(next[r][c]);
    }
    while (col.length < GRID_SIZE) {
      col.unshift({ type: randomGem(), id: `new-${Math.random().toString(36).slice(2, 8)}` });
    }
    for (let r = 0; r < GRID_SIZE; r++) next[r][c] = col[r];
  }
  return next;
}

// ─── Component ────────────────────────────────────────────────────────────────
interface GemDuelGameProps {
  onWin: () => void;
  onLose: () => void;
}

export default function GemDuelGame({ onWin, onLose }: GemDuelGameProps) {
  const [phase, setPhase] = useState<Phase>('intro');
  const [grid, setGrid] = useState<Gem[][]>(() => makeGrid());
  const [selected, setSelected] = useState<{ r: number; c: number } | null>(null);
  const [playerScore, setPlayerScore] = useState(0);
  const [aiScore, setAiScore] = useState(0);
  const [turnsLeft, setTurnsLeft] = useState(10);
  const [abilities, setAbilities] = useState<AbilityCard[]>(INITIAL_ABILITIES);
  const [activeAbility, setActiveAbility] = useState<AbilityCard | null>(null);
  const [stickers, setStickers] = useState<StickerSlot[]>(STICKER_ALBUM);
  const [combo, setCombo] = useState(0);
  const [lastMatchCount, setLastMatchCount] = useState(0);
  const [animatingCells, setAnimatingCells] = useState<Set<string>>(new Set());
  const [showAbilityPanel, setShowAbilityPanel] = useState(false);
  const [aiThinking, setAiThinking] = useState(false);
  const [newStickerEarned, setNewStickerEarned] = useState<StickerSlot | null>(null);
  const [abilityUsedMsg, setAbilityUsedMsg] = useState<string | null>(null);
  const processingRef = useRef(false);

  // ─── Sticker unlock logic ──────────────────────────────────────────────────
  const tryUnlockSticker = useCallback((matchCount: number, isCombo: boolean, hasDiamond: boolean) => {
    setStickers(prev => {
      const updated = [...prev];
      let earned: StickerSlot | null = null;
      if (!updated[0].filled) { updated[0] = { ...updated[0], filled: true }; earned = updated[0]; }
      else if (matchCount >= 4 && !updated[1].filled) { updated[1] = { ...updated[1], filled: true }; earned = updated[1]; }
      else if (hasDiamond && !updated[2].filled) { updated[2] = { ...updated[2], filled: true }; earned = updated[2]; }
      else if (isCombo && !updated[3].filled) { updated[3] = { ...updated[3], filled: true }; earned = updated[3]; }
      if (earned) setNewStickerEarned(earned);
      return updated;
    });
  }, []);

  // ─── Unlock ability card when sticker page fills ───────────────────────────
  const checkAbilityUnlock = useCallback((stickerList: StickerSlot[]) => {
    const filled = stickerList.filter(s => s.filled).length;
    setAbilities(prev => prev.map((ab, i) => {
      if (i === 0) return ab; // row_wipe always starts unlocked
      if (i === 1 && filled >= 2) return { ...ab, uses: Math.max(ab.uses, 1) };
      if (i === 2 && filled >= 4) return { ...ab, uses: Math.max(ab.uses, 1) };
      if (i === 3 && filled >= 6) return { ...ab, uses: Math.max(ab.uses, 1) };
      return ab;
    }));
  }, []);

  useEffect(() => {
    checkAbilityUnlock(stickers);
  }, [stickers, checkAbilityUnlock]);

  // ─── Process matches cascade ───────────────────────────────────────────────
  const processMatches = useCallback((g: Gem[][], comboDepth = 0): Promise<{ finalGrid: Gem[][]; points: number }> => {
    return new Promise(resolve => {
      const matched = findMatches(g);
      if (matched.size === 0) {
        resolve({ finalGrid: g, points: 0 });
        return;
      }
      const hasDiamond = [...matched].some(key => {
        const [r, c] = key.split('-').map(Number);
        return g[r]?.[c]?.type === 'diamond';
      });
      const pts = matched.size * 10 * (comboDepth + 1);
      setAnimatingCells(matched);
      setLastMatchCount(matched.size);
      setCombo(comboDepth + 1);
      tryUnlockSticker(matched.size, comboDepth > 0, hasDiamond);

      setTimeout(() => {
        const marked = g.map(row => row.map(gem => ({ ...gem })));
        matched.forEach(key => {
          const [r, c] = key.split('-').map(Number);
          if (marked[r]?.[c]) marked[r][c].matched = true;
        });
        const collapsed = collapseGrid(marked);
        setGrid(collapsed);
        setAnimatingCells(new Set());

        setTimeout(() => {
          processMatches(collapsed, comboDepth + 1).then(({ finalGrid, points: extraPts }) => {
            resolve({ finalGrid, points: pts + extraPts });
          });
        }, 300);
      }, 400);
    });
  }, [tryUnlockSticker]);

  // ─── Swap gems ────────────────────────────────────────────────────────────
  const handleGemTap = useCallback(async (r: number, c: number) => {
    if (processingRef.current || phase !== 'playing') return;

    // Ability targeting mode
    if (activeAbility) {
      processingRef.current = true;
      let newGrid = grid.map(row => row.map(g => ({ ...g })));

      if (activeAbility.action === 'row_wipe') {
        for (let col = 0; col < GRID_SIZE; col++) newGrid[r][col].matched = true;
        setAbilityUsedMsg(`💥 Row ${r + 1} blasted!`);
      } else if (activeAbility.action === 'col_wipe') {
        for (let row = 0; row < GRID_SIZE; row++) newGrid[row][c].matched = true;
        setAbilityUsedMsg(`⚡ Column ${c + 1} crushed!`);
      } else if (activeAbility.action === 'gem_clear') {
        const targetType = grid[r][c].type;
        for (let row = 0; row < GRID_SIZE; row++)
          for (let col = 0; col < GRID_SIZE; col++)
            if (newGrid[row][col].type === targetType) newGrid[row][col].matched = true;
        setAbilityUsedMsg(`🌀 All ${GEM_EMOJIS[grid[r][c].type]} cleared!`);
      } else if (activeAbility.action === 'bomb_drop') {
        for (let dr = -1; dr <= 1; dr++)
          for (let dc = -1; dc <= 1; dc++)
            if (newGrid[r + dr]?.[c + dc]) newGrid[r + dr][c + dc].matched = true;
        setAbilityUsedMsg(`💣 3×3 explosion!`);
      }

      const matchedKeys = new Set<string>();
      newGrid.forEach((row, ri) => row.forEach((gem, ci) => { if (gem.matched) matchedKeys.add(`${ri}-${ci}`); }));
      setAnimatingCells(matchedKeys);
      const pts = matchedKeys.size * 15;

      setAbilities(prev => prev.map(ab => ab.id === activeAbility.id ? { ...ab, uses: ab.uses - 1 } : ab));
      setActiveAbility(null);

      setTimeout(() => {
        const collapsed = collapseGrid(newGrid);
        setGrid(collapsed);
        setAnimatingCells(new Set());
        setPlayerScore(s => s + pts);
        setTimeout(() => setAbilityUsedMsg(null), 1500);
        processingRef.current = false;
        // AI turn after ability
        doAiTurn(collapsed);
      }, 500);
      return;
    }

    if (!selected) {
      setSelected({ r, c });
      return;
    }

    let dr = Math.abs(r - selected.r);
    let dc = Math.abs(c - selected.c);
    if ((dr === 1 && dc === 0) || (dr === 0 && dc === 1)) {
      processingRef.current = true;
      const newGrid = grid.map(row => row.map(g => ({ ...g })));
      const tmp = newGrid[r][c];
      newGrid[r][c] = newGrid[selected.r][selected.c];
      newGrid[selected.r][selected.c] = tmp;
      setSelected(null);

      const matched = findMatches(newGrid);
      if (matched.size === 0) {
        // Invalid swap — revert
        const reverted = grid.map(row => row.map(g => ({ ...g })));
        setGrid(reverted);
        processingRef.current = false;
        return;
      }

      setGrid(newGrid);
      const { finalGrid, points } = await processMatches(newGrid);
      setPlayerScore(s => s + points);
      setTurnsLeft(t => {
        const newT = t - 1;
        if (newT <= 0) {
          setTimeout(() => endGame(playerScore + points, aiScore), 400);
        }
        return newT;
      });
      processingRef.current = false;
      if (turnsLeft > 1) doAiTurn(finalGrid);
    } else {
      setSelected({ r, c });
    }
  }, [grid, selected, phase, activeAbility, processMatches, playerScore, aiScore, turnsLeft]);

  // ─── AI turn ──────────────────────────────────────────────────────────────
  const doAiTurn = useCallback((currentGrid: Gem[][]) => {
    setAiThinking(true);
    setTimeout(() => {
      // AI finds best swap
      let bestPts = 0;
      let bestSwap: [number, number, number, number] | null = null;
      for (let r = 0; r < GRID_SIZE; r++) {
        for (let c = 0; c < GRID_SIZE; c++) {
          const swaps: [number, number][] = [[r + 1, c], [r, c + 1]];
          for (const [nr, nc] of swaps) {
            if (nr >= GRID_SIZE || nc >= GRID_SIZE) continue;
            const g = currentGrid.map(row => row.map(gem => ({ ...gem })));
            const tmp = g[r][c]; g[r][c] = g[nr][nc]; g[nr][nc] = tmp;
            const m = findMatches(g);
            if (m.size > bestPts) { bestPts = m.size; bestSwap = [r, c, nr, nc]; }
          }
        }
      }
      if (bestSwap && bestPts > 0) {
        const [r1, c1, r2, c2] = bestSwap;
        const g = currentGrid.map(row => row.map(gem => ({ ...gem })));
        const tmp = g[r1][c1]; g[r1][c1] = g[r2][c2]; g[r2][c2] = tmp;
        const matched = findMatches(g);
        const aiPts = matched.size * 8; // AI scores slightly less
        setAiScore(s => s + aiPts);
        matched.forEach(key => {
          const [r, c] = key.split('-').map(Number);
          if (g[r]?.[c]) g[r][c].matched = true;
        });
        const collapsed = collapseGrid(g);
        setGrid(collapsed);
      }
      setAiThinking(false);
    }, 800 + Math.random() * 600);
  }, []);

  // ─── End game ─────────────────────────────────────────────────────────────
  const endGame = useCallback((pScore: number, aScore: number) => {
    setPhase('result');
    if (pScore > aScore) {
      setStickers(prev => {
        const updated = [...prev];
        if (!updated[4].filled) updated[4] = { ...updated[4], filled: true };
        if (!updated[5].filled) updated[5] = { ...updated[5], filled: true };
        return updated;
      });
      setTimeout(onWin, 1200);
    } else {
      setTimeout(onLose, 1200);
    }
  }, [onWin, onLose]);

  useEffect(() => {
    if (turnsLeft === 0 && phase === 'playing') {
      endGame(playerScore, aiScore);
    }
  }, [turnsLeft, phase, playerScore, aiScore, endGame]);

  // ─── Render: Intro ────────────────────────────────────────────────────────
  if (phase === 'intro') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 20, padding: '24px 16px', textAlign: 'center' }}>
        <div style={{ fontSize: 56 }}>💎</div>
        <div>
          <h2 style={{ color: '#ffd23f', fontWeight: 900, fontSize: '1.5rem', margin: 0, letterSpacing: '-0.02em' }}>Gem Duel</h2>
          <p style={{ color: '#a78bfa', fontSize: '0.75rem', fontWeight: 700, margin: '4px 0 0', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Match Masters Style · PvP Duel</p>
        </div>
        <div style={{ background: 'rgba(255,210,63,0.06)', border: '1px solid rgba(255,210,63,0.2)', borderRadius: 16, padding: '16px 20px', maxWidth: 320, textAlign: 'left' }}>
          {[
            ['💎', 'Match 3+ gems to score points'],
            ['⚔️', 'Beat the AI opponent in 10 turns'],
            ['🃏', 'Earn ability cards via sticker album'],
            ['💥', 'Use abilities to wipe rows & columns'],
          ].map(([icon, text]) => (
            <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <span style={{ fontSize: 18 }}>{icon}</span>
              <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem', fontWeight: 600 }}>{text}</span>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {GEM_TYPES.map(t => (
            <span key={t} style={{ fontSize: 24, filter: 'drop-shadow(0 0 8px ' + GEM_COLORS[t] + ')' }}>{GEM_EMOJIS[t]}</span>
          ))}
        </div>
        <button
          onClick={() => setPhase('playing')}
          style={{
            background: 'linear-gradient(135deg, #ffd23f 0%, #ff9500 50%, #ff3b6b 100%)',
            border: 'none', borderRadius: 14, padding: '14px 40px',
            color: '#0a0a0f', fontWeight: 900, fontSize: '1rem', cursor: 'pointer',
            boxShadow: '0 0 30px rgba(255,210,63,0.4)',
            letterSpacing: '0.05em',
          }}
        >
          ⚔️ Start Duel
        </button>
      </div>
    );
  }

  // ─── Render: Result ───────────────────────────────────────────────────────
  if (phase === 'result') {
    const won = playerScore > aiScore;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 16, padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 64 }}>{won ? '🏆' : '💀'}</div>
        <h2 style={{ color: won ? '#ffd23f' : '#ff3b6b', fontWeight: 900, fontSize: '1.6rem', margin: 0 }}>
          {won ? 'Duel Won!' : 'Duel Lost'}
        </h2>
        <div style={{ display: 'flex', gap: 24 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#36e2a4', fontWeight: 900, fontSize: '2rem' }}>{playerScore}</div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', fontWeight: 700 }}>YOU</div>
          </div>
          <div style={{ color: 'rgba(255,255,255,0.2)', fontWeight: 900, fontSize: '1.5rem', alignSelf: 'center' }}>vs</div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#ff3b6b', fontWeight: 900, fontSize: '2rem' }}>{aiScore}</div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', fontWeight: 700 }}>AI</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center', maxWidth: 280 }}>
          {stickers.map(s => (
            <div key={s.id} style={{ fontSize: 24, opacity: s.filled ? 1 : 0.2, filter: s.filled ? 'drop-shadow(0 0 6px gold)' : 'none', transition: 'all 0.4s' }} title={s.label}>
              {s.emoji}
            </div>
          ))}
        </div>
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem', fontWeight: 600 }}>
          {stickers.filter(s => s.filled).length}/{stickers.length} stickers collected
        </p>
      </div>
    );
  }

  // ─── Render: Playing ──────────────────────────────────────────────────────
  const filledStickers = stickers.filter(s => s.filled).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: 420, margin: '0 auto', padding: '8px 12px', gap: 8, userSelect: 'none' }}>

      {/* Score bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.04)', borderRadius: 12, padding: '8px 12px' }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ color: '#36e2a4', fontWeight: 900, fontSize: '1.3rem', lineHeight: 1 }}>{playerScore}</div>
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.1em' }}>YOU</div>
        </div>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ color: '#ffd23f', fontWeight: 900, fontSize: '0.9rem' }}>{turnsLeft}</div>
          <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.08em' }}>TURNS LEFT</div>
          {aiThinking && <div style={{ color: '#ff9500', fontSize: '0.55rem', fontWeight: 700, marginTop: 2 }}>AI thinking…</div>}
        </div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ color: '#ff3b6b', fontWeight: 900, fontSize: '1.3rem', lineHeight: 1 }}>{aiScore}</div>
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.1em' }}>AI 🤖</div>
        </div>
      </div>

      {/* Combo / ability flash */}
      {(combo > 1 || abilityUsedMsg) && (
        <div style={{ textAlign: 'center', color: '#ffd23f', fontWeight: 900, fontSize: '0.85rem', letterSpacing: '0.05em', animation: 'streakBump 0.3s ease' }}>
          {abilityUsedMsg || `🔥 ${combo}× COMBO! +${lastMatchCount * 10 * combo}pts`}
        </div>
      )}

      {/* New sticker toast */}
      {newStickerEarned && (
        <div
          style={{ background: 'rgba(255,210,63,0.12)', border: '1px solid rgba(255,210,63,0.4)', borderRadius: 10, padding: '6px 12px', textAlign: 'center', fontSize: '0.75rem', fontWeight: 700, color: '#ffd23f', cursor: 'pointer' }}
          onClick={() => setNewStickerEarned(null)}
        >
          ✨ New sticker: {newStickerEarned.emoji} {newStickerEarned.label}!
        </div>
      )}

      {/* Active ability indicator */}
      {activeAbility && (
        <div style={{ background: `${activeAbility.color}22`, border: `1px solid ${activeAbility.color}66`, borderRadius: 10, padding: '6px 12px', textAlign: 'center', fontSize: '0.75rem', fontWeight: 700, color: activeAbility.color }}>
          {activeAbility.emoji} {activeAbility.name} — tap a gem to target
        </div>
      )}

      {/* Gem Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`, gap: 3, background: 'rgba(255,255,255,0.03)', borderRadius: 16, padding: 8, border: '1px solid rgba(255,255,255,0.06)' }}>
        {grid.map((row, r) =>
          row.map((gem, c) => {
            const key = `${r}-${c}`;
            const isSelected = selected?.r === r && selected?.c === c;
            const isAnimating = animatingCells.has(key);
            const gemColor = GEM_COLORS[gem.type];
            return (
              <button
                key={gem.id}
                onClick={() => handleGemTap(r, c)}
                style={{
                  aspectRatio: '1',
                  borderRadius: 8,
                  border: isSelected ? `2px solid ${gemColor}` : '2px solid transparent',
                  background: isSelected ? `${gemColor}33` : isAnimating ? `${gemColor}55` : 'rgba(255,255,255,0.04)',
                  fontSize: 'clamp(14px, 3.5vw, 22px)',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.15s ease',
                  transform: isSelected ? 'scale(1.15)' : isAnimating ? 'scale(0.8)' : 'scale(1)',
                  boxShadow: isSelected ? `0 0 12px ${gemColor}88` : isAnimating ? `0 0 8px ${gemColor}66` : 'none',
                  opacity: isAnimating ? 0.4 : 1,
                  padding: 0,
                }}
                aria-label={`${gem.type} gem at row ${r + 1} column ${c + 1}`}
              >
                {GEM_EMOJIS[gem.type]}
              </button>
            );
          })
        )}
      </div>

      {/* Sticker album progress */}
      <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 12, padding: '8px 12px', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>📖 Sticker Album</span>
          <span style={{ color: '#ffd23f', fontSize: '0.6rem', fontWeight: 700 }}>{filledStickers}/{stickers.length}</span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {stickers.map(s => (
            <div
              key={s.id}
              title={s.label}
              style={{
                flex: 1, aspectRatio: '1', borderRadius: 8,
                background: s.filled ? 'rgba(255,210,63,0.12)' : 'rgba(255,255,255,0.04)',
                border: s.filled ? '1px solid rgba(255,210,63,0.4)' : '1px solid rgba(255,255,255,0.08)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 'clamp(10px, 2.5vw, 16px)',
                opacity: s.filled ? 1 : 0.25,
                transition: 'all 0.4s ease',
                filter: s.filled ? 'drop-shadow(0 0 4px gold)' : 'none',
              }}
            >
              {s.emoji}
            </div>
          ))}
        </div>
      </div>

      {/* Ability cards */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>🃏 Ability Cards</span>
          <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.6rem' }}>Fill stickers to unlock</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
          {abilities.map(ab => {
            const unlocked = ab.uses > 0;
            const isActive = activeAbility?.id === ab.id;
            return (
              <button
                key={ab.id}
                onClick={() => {
                  if (!unlocked || phase !== 'playing') return;
                  setActiveAbility(isActive ? null : ab);
                }}
                style={{
                  borderRadius: 10, padding: '8px 4px',
                  background: isActive ? `${ab.color}33` : unlocked ? `${ab.color}11` : 'rgba(255,255,255,0.03)',
                  border: isActive ? `2px solid ${ab.color}` : unlocked ? `1px solid ${ab.color}44` : '1px solid rgba(255,255,255,0.08)',
                  cursor: unlocked ? 'pointer' : 'not-allowed',
                  opacity: unlocked ? 1 : 0.35,
                  transition: 'all 0.2s ease',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                  transform: isActive ? 'scale(1.05)' : 'scale(1)',
                  boxShadow: isActive ? `0 0 12px ${ab.color}66` : 'none',
                }}
                aria-label={`${ab.name}: ${ab.description}`}
              >
                <span style={{ fontSize: 18 }}>{ab.emoji}</span>
                <span style={{ color: unlocked ? ab.color : 'rgba(255,255,255,0.3)', fontSize: '0.55rem', fontWeight: 700, textAlign: 'center', lineHeight: 1.2 }}>{ab.name}</span>
                {unlocked && <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.5rem' }}>{ab.uses}/{ab.maxUses}</span>}
                {!unlocked && <span style={{ fontSize: 10 }}>🔒</span>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
